
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { api } from './api.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { hasRegressions, isGoodToGo } from './data-table-providers/pullsProvider.js';
import { FeatureTestsReportState } from './features/feature-tests-report.js';
import { forgeUtils } from './forgeUtils.js';
import { renderRelativeDate } from './fragments.js';
import { PageRun } from './page-run.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {

  timelineSplit: URLOptions.timelineSplit,

  query: URLOptions.query,

  /* ---- */
  plane: URLOptions.plane,
  page: URLOptions.page,
  pageSize: URLOptions.pageSize,
  sortName: URLOptions.sortName,
  sortDirection: URLOptions.sortDirection,
  historyType: URLOptions.historyType,
};

@customElement('page-pull')
export class PagePull extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/pull/:pull',
        '/:org/:project/pull/:pull/'
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-pull
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
          .pullRequestNumber=${groups?.pull}
        ></page-pull>
      `),
    }];
  }

  static url(options: Omit<FeatureTestsReportState, 'timelineSplit'> & {
    timelineSplit?: EnvGroup,
    orgSlug: string,
    projectSlug: string,
    pullRequest: number,
    snapshotId: WireTypes.DataSnapshotId | undefined,
  }) {
    const pathname = ['', options.orgSlug, options.projectSlug, 'pull', options.pullRequest].join('/');
    return projectPlatformUrl(pathname, URLState.toSearchParams(pageSchema, {
      timelineSplit: options.timelineSplit,
      page: options.page,
      pageSize: options.pageSize,
      plane: options.plane,
      query: options.query,
      sortDirection: options.sortDirection,
      sortName: options.sortName,
      historyType: options.historyType,
    }), options.snapshotId);
  }

  private _urlState = new URLState(this, pageSchema);

  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;
  @property({ attribute: false }) pullRequestNumber?: string;

  private _pullRequestTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId, this.pullRequestNumber,
    ] as const,
    task: async ([project, snapshotId, prNumber], { signal }) => {
      CHECK(project && snapshotId && prNumber);
      const number = parseInt(prNumber, 10);
      CHECK(!isNaN(number));
      const result = await api.project.pull.GET({
        number,
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        timeZoneId: project.timezone,
      }, { signal });
      CHECK(result);
      return result;
    }
  });

  private _commitOptions = new Task(this, {
    args: () => [
      this._pullRequestTask.value,
    ] as const,
    task: async ([pullRequest]) => {
      CHECK(pullRequest && pullRequest.commitStats);
      return {
        head: pullRequest.commitStats.commit.commitId,
        maxCount: 1,
      } satisfies WireTypes.ListCommitOptions;
    }
  });

  private _allEnvironments = tasks.allEnvironments(this, {
    commitOptions: () => this._commitOptions.value,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // "All Environments" of the pull request's commit and the history of its base branch.
  private _defaultSplit = tasks.defaultSplit(this, {
    preset: () => this._pullRequestTask.value ? { type: 'pr', number: this._pullRequestTask.value.number } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // Once the default is known, a URL that spells it out loses it, without a new history entry.
  private _canonicalUrlTask = new Task(this, {
    args: () => [this._urlState.state.timelineSplit, this._defaultSplit.value] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        this._urlState.update({ timelineSplit: undefined }, { replace: true });
    },
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug, this._project, this.pullRequestNumber, this._pullRequestTask.value] as const,
    metadata: ([orgSlug, projectSlug, project, pullRequestNumber, pullRequest]) => orgSlug && projectSlug && pullRequestNumber ? pageMetadata({
      orgSlug, projectSlug,
      section: `${forgeUtils.pullRequestLabel(project)} #${pullRequestNumber}${pullRequest ? `: ${pullRequest.title}` : ''}`,
    }) : undefined,
  });

  override render() {
    const project = this._project;
    if (!project)
      return nothing;

    const pr = this._pullRequestTask.value;  
    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${'pulls'}
      ></project-header>

      <project-body wide>
        ${pr ? this._renderContent(project, pr) : nothing}
      </project-body>
      <app-footer></app-footer>
    `;
  }

  private _renderContent(project: WireTypes.Project, pr: WireTypes.PullRequestStats) {
    const split = this._urlState.state.timelineSplit ?? this._defaultSplit.value;

    const tag = isGoodToGo(pr) ? html`<sl-tag size=small pill variant=success>Mergeable</sl-tag>` :
      hasRegressions(pr) ? html`<sl-tag pill size=small  variant=danger>Has Regressions</sl-tag>` :
      undefined;

    return html`
      <h-box style="font-size: var(--sl-font-size-2x-large);">
        <fk-pr-icon .state=${pr.state}></fk-pr-icon>
        <v-box>
          <div>
            <span>${pr.title}</span>
          </div>
          <h-box style=${styleMap({
            'font-size': 'var(--sl-font-size-small)',
            'gap': 'var(--sl-spacing-x-small)',
            'color': 'var(--sl-color-neutral-600)',
          })}>
            <div>
              ${forgeUtils.hasSourceUrl(project) ? html`
                <a href=${forgeUtils.pullRequestUrl(project, String(pr.number))} target=_blank title=${`View on ${forgeUtils.providerName(project)}`}>#${pr.number}</a>
              ` : html`#${pr.number}`}
              opened ${renderRelativeDate(pr.createdTimestamp)}
              by
              <sl-avatar image=${pr.avatar_url} label=${pr.author} loading=lazy style=${styleMap({
                '--size': '16px',
              })}></sl-avatar>
              ${pr.author}
            </div>
            <sl-icon name=dot></sl-icon>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              <branch-label .name=${pr.baseRef}></branch-label>
              <sl-icon name=arrow-left></sl-icon>
              <branch-label .name=${pr.headRef}></branch-label>
            </h-box>
            <sl-icon name=dot></sl-icon>
            <runstats-selector
              .commitStats=${pr.commitStats}
              .runlinker=${PageRun.linkifier(project, this._snapshotId)}
            ></runstats-selector>
            <sl-icon name=dot></sl-icon>
            <forge-link .pr=${pr.number}></forge-link>
            ${tag ? html`
              <sl-icon name=dot></sl-icon>
              ${tag}
            ` : nothing}
          </h-box>
        </v-box>
      </h-box>

      ${this._commitOptions.value && split ? html`
        <timeline-selector multiple
          .envs=${this._allEnvironments.value}
          .split=${split}
          .defaultSplit=${this._defaultSplit.value}
          @fk-select=${(event: CustomEvent<EnvGroup>) => {
            this._urlState.update({
              timelineSplit: event.detail,
              page: 0,
            });
          }}
        ></timeline-selector>

        <feature-tests-report
          .commitOptions=${this._commitOptions.value}
          .customHistoryHead=${pr.historyHead}
          .historyBranchName=${pr.baseRef}
          .state=${{
            timelineSplit: split,
            page: this._urlState.state.page,
            pageSize: this._urlState.state.pageSize,
            plane: this._urlState.state.plane,
            query: this._urlState.state.query,
            sortDirection: this._urlState.state.sortDirection,
            sortName: this._urlState.state.sortName,
            historyType: this._urlState.state.historyType,
          } satisfies FeatureTestsReportState}
          .linkifier=${(options: FeatureTestsReportState) => PagePull.url({
            ...options,
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            pullRequest: pr.number,
            snapshotId: this._snapshotId,
          })}
        ></feature-tests-report>
      ` : this._commitOptions.value ? nothing : html`
        <sl-divider></sl-divider>
        <h1 class=no-test-runs>No test runs found for this ${forgeUtils.pullRequestLabel(this._project).toLowerCase()}.</h1>
      `}
    `    
  }

  static styles = [pageStyles, linkStyles, css`
    .no-test-runs {
      display: flex;
      align-items: center;
      color: var(--fk-color-border);
    }
  `]
}
